import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Clock, RefreshCw, Calendar, Users } from 'lucide-react';
import DataGrid from '../../components/datagrid/DataGrid';
import Notice from '../../components/common/Notice';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import { formatTL } from '../../utils/money';
import './WorkdayEarningsPage.css';

// CourierCostsPage.jsx'teki EARNING_MODES ile aynı etiketler (tutarlılık için).
const EARNING_MODE_LABELS = {
    0: 'Taşıdığı paket kadar',
    1: 'Çalıştığı saat kadar',
    2: 'Günlük sabit yevmiye',
};

// Backend CommissionStatus enum'u ile birebir (Enums.cs).
const STATUS_INFO = {
    0: { label: 'Beklemede', tone: 'pending' },
    1: { label: 'Hesaplandı', tone: 'info' },
    2: { label: 'Onaylandı', tone: 'info' },
    3: { label: 'Faturalandı', tone: 'info' },
    4: { label: 'Ödendi', tone: 'active' },
    5: { label: 'İptal edildi', tone: 'inactive' },
    6: { label: 'İade edildi', tone: 'inactive' },
};

const DEBOUNCE_MS = 700;

function defaultDateRange() {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    return {
        start: thirtyDaysAgo.toISOString().slice(0, 10),
        end: today.toISOString().slice(0, 10),
    };
}

/** @param embedded Hakedişler ekranında sekme olarak açıldığında kendi başlığını çizmez. */
const WorkdayEarningsPage = ({ embedded = false }) => {
    const initialRange = defaultDateRange();

    // Ham filtre girdileri (input'lara bağlı) — debounce sonrası "uygulanan" filtrelere yansır.
    const [startDateInput, setStartDateInput] = useState(initialRange.start);
    const [endDateInput, setEndDateInput] = useState(initialRange.end);
    const [courierIdInput, setCourierIdInput] = useState('');

    // Fiilen istekte kullanılan (debounce edilmiş) filtreler.
    const [startDate, setStartDate] = useState(initialRange.start);
    const [endDate, setEndDate] = useState(initialRange.end);
    const [courierId, setCourierId] = useState('');

    const [couriers, setCouriers] = useState([]);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortField, setSortField] = useState('workDate');
    const [sortOrder, setSortOrder] = useState('desc');

    // Çift istek koruması: requestId ile eski cevaplar yok sayılır, AbortController ile
    // önceki bekleyen istek iptal edilmeye çalışılır.
    const requestIdRef = useRef(0);
    const abortRef = useRef(null);
    const debounceRef = useRef(null);

    const formatDate = (value) => {
        if (!value) return '-';
        return new Date(value).toLocaleDateString('tr-TR');
    };

    const formatHours = (value) => {
        if (value == null) return '-';
        return `${Number(value).toFixed(2)} sa`;
    };

    const formatMinutes = (value) => {
        if (value == null) return '-';
        const minutes = Number(value);
        if (minutes < 60) return `${Math.round(minutes)} dk`;
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return `${hours} sa ${mins} dk`;
    };

    const fetchCouriers = useCallback(async () => {
        try {
            const res = await apiService.post(API_ENDPOINTS.FLEET_PANEL.MEMBERS_LIST, {
                page: 1,
                pageSize: 500,
                filterModel: { IsActive: { filter: 'true', type: 'eq' } }
            });
            if (res.success && res.data?.items) {
                setCouriers(res.data.items.map(m => ({
                    id: m.courierId,
                    name: m.courierName || m.fullName || '-'
                })));
            }
        } catch {}
    }, []);

    useEffect(() => { fetchCouriers(); }, [fetchCouriers]);

    // Filtre girdileri değiştikçe 700ms debounce ile "uygulanan" filtrelere aktar.
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setStartDate(startDateInput);
            setEndDate(endDateInput);
            setCourierId(courierIdInput);
            setCurrentPage(1);
        }, DEBOUNCE_MS);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [startDateInput, endDateInput, courierIdInput]);

    const buildFilterModel = useCallback(() => {
        const fm = {};
        if (startDate && endDate) {
            fm.workDate = { filter: startDate, filterTo: endDate, type: 'inRange' };
        }
        if (courierId) {
            fm.courierId = { filter: courierId, type: 'eq' };
        }
        return fm;
    }, [startDate, endDate, courierId]);

    const fetchEarnings = useCallback(async () => {
        const myRequestId = ++requestIdRef.current;
        try { abortRef.current?.abort(); } catch {}
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        setError('');
        try {
            const res = await apiService.post(API_ENDPOINTS.FLEET_PANEL.WORKDAY_EARNINGS_LIST, {
                page: currentPage,
                pageSize,
                sortModels: sortField ? [{ colId: sortField, sort: sortOrder }] : null,
                filterModel: buildFilterModel()
            });

            if (requestIdRef.current !== myRequestId) return; // eski cevap — yok say

            if (res.success && res.data) {
                const data = res.data.data || res.data;
                setRows(data.items || []);
                setTotalRecords(data.totalCount || 0);
            } else {
                setError(res.error || 'Veriler yüklenemedi');
            }
        } catch {
            if (requestIdRef.current !== myRequestId) return;
            setError('Bir hata oluştu');
        } finally {
            if (requestIdRef.current === myRequestId) setLoading(false);
        }
    }, [currentPage, pageSize, sortField, sortOrder, buildFilterModel]);

    useEffect(() => { fetchEarnings(); }, [fetchEarnings]);

    const handleSort = (field, order) => { setSortField(field); setSortOrder(order); };
    const handlePageSizeChange = (size) => { setPageSize(size); setCurrentPage(1); };

    const columns = useMemo(() => [
        { field: 'courierFullName', header: 'Kurye', sortable: false },
        { field: 'workDate', header: 'Tarih', sortable: true, render: (row) => formatDate(row.workDate) },
        { field: 'earningMode', header: 'Ücret Modeli', sortable: false, render: (row) => EARNING_MODE_LABELS[row.earningMode] ?? 'Bilinmiyor' },
        { field: 'hoursCounted', header: 'Saat', sortable: true, render: (row) => formatHours(row.hoursCounted) },
        { field: 'actualWorkedMinutes', header: 'Fiili Dakika', sortable: true, render: (row) => formatMinutes(row.actualWorkedMinutes) },
        { field: 'deliveryEarningOfDay', header: 'Teslimat Kazancı', sortable: true, render: (row) => formatTL(row.deliveryEarningOfDay) },
        { field: 'amount', header: 'Tutar', sortable: true, render: (row) => <span className="we-amount-highlight">{formatTL(row.amount)}</span> },
        {
            field: 'status', header: 'Durum', sortable: true,
            render: (row) => {
                const st = STATUS_INFO[row.status] || { label: 'Bilinmiyor', tone: 'unknown' };
                return <span className={`status-badge ${st.tone}`}>{st.label}</span>;
            }
        },
    ], []);

    const renderCard = (row) => {
        const st = STATUS_INFO[row.status] || { label: 'Bilinmiyor', tone: 'unknown' };
        return (
            <div className="we-card">
                <div className="we-card-header">
                    <div>
                        <h3>{row.courierFullName || '-'}</h3>
                        <p>{formatDate(row.workDate)}, {EARNING_MODE_LABELS[row.earningMode] ?? 'Bilinmiyor'}</p>
                    </div>
                    <span className={`status-badge ${st.tone}`}>{st.label}</span>
                </div>
                <div className="we-card-stats">
                    <div className="we-card-stat">
                        <span>Saat</span>
                        <strong>{formatHours(row.hoursCounted)}</strong>
                    </div>
                    <div className="we-card-stat">
                        <span>Fiili Dakika</span>
                        <strong>{formatMinutes(row.actualWorkedMinutes)}</strong>
                    </div>
                    <div className="we-card-stat">
                        <span>Teslimat Kazancı</span>
                        <strong>{formatTL(row.deliveryEarningOfDay)}</strong>
                    </div>
                    <div className="we-card-stat highlight">
                        <span>Tutar</span>
                        <strong>{formatTL(row.amount)}</strong>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="we-container">
            {!embedded && (
                <div className="page-header">
                    <div className="header-left">
                        <div className="page-icon"><Clock size={20} /></div>
                        <div className="page-title">
                            <h1>Mesai Hakedişleri</h1>
                            <p>Kuryelerinizin saatlik/günlük mesai bazlı hakediş kayıtlarını inceleyin</p>
                        </div>
                    </div>
                    <button className="btn btn-secondary" onClick={fetchEarnings}>
                        <RefreshCw size={16} />
                        Yenile
                    </button>
                </div>
            )}

            <div className="we-filter-bar">
                <div className="form-group">
                    <label><Calendar size={12} /> Başlangıç Tarihi</label>
                    <input
                        type="date"
                        value={startDateInput}
                        max={endDateInput}
                        onChange={(e) => setStartDateInput(e.target.value)}
                    />
                </div>
                <div className="form-group">
                    <label><Calendar size={12} /> Bitiş Tarihi</label>
                    <input
                        type="date"
                        value={endDateInput}
                        min={startDateInput}
                        onChange={(e) => setEndDateInput(e.target.value)}
                    />
                </div>
                <div className="form-group">
                    <label><Users size={12} /> Kurye</label>
                    <select value={courierIdInput} onChange={(e) => setCourierIdInput(e.target.value)}>
                        <option value="">Tüm Kuryeler</option>
                        {couriers.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {error && (
                <Notice tone="error" title="Kayıtlar yüklenemedi">
                    {error} Bağlantınızı kontrol edip yeniden deneyin.
                </Notice>
            )}

            <div className="we-grid-section">
                <div className="we-grid-header">
                    <h2><Clock size={20} /> Hakediş Kayıtları</h2>
                    <div className="we-grid-tools">
                        <span className="we-grid-count">{totalRecords} kayıt</span>
                        {embedded && (
                            <button className="btn btn-secondary btn-sm" onClick={fetchEarnings}>
                                <RefreshCw size={14} />
                                Yenile
                            </button>
                        )}
                    </div>
                </div>
                <DataGrid
                    data={rows}
                    columns={columns}
                    loading={loading}
                    error={error}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalRecords={totalRecords}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={handlePageSizeChange}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    renderCard={renderCard}
                    emptyIcon={Clock}
                    emptyMessage="Seçilen aralıkta mesai kaydı yok."
                    emptyHint={
                        <ul>
                            <li>
                                Mesai kaydı yalnız ücret modeli <strong>saatlik</strong> ya da{' '}
                                <strong>yövmiye</strong> olan kuryeler için oluşur.{' '}
                                <strong>Taşıdığı paket kadar</strong> çalışan kuryenin kaydı burada değil,{' '}
                                <strong>Kurye hakedişi</strong> sekmesinde görünür.
                            </li>
                            <li>
                                Kurye, kaydın gününde <strong>filonuza onaylı üye</strong> olmalı. Filodan
                                ayrıldıktan sonraki günler filonun listesine düşmez.
                            </li>
                            <li>O gün <strong>vardiya açılmamışsa</strong> satır üretilmez.</li>
                            <li>Günün kaydı ertesi gün <strong>03:00</strong>'te yazılır — bugünün satırı yarın görünür.</li>
                        </ul>
                    }
                    loadingMessage="Yükleniyor..."
                />
            </div>
        </div>
    );
};

export default WorkdayEarningsPage;
